/**
 * Allowlist of official MTG eternal-legal creature subtypes. Used as a
 * client-side defensive filter when the kindred aggregator in the backend
 * leaks non-creature-type tokens (e.g. card text fragments like "while",
 * "searching", "win"). Remove this filter once the backend pipeline is
 * fixed upstream.
 *
 * Sourced from Comprehensive Rules 205.3m (see Draftsim / MTG Wiki
 * creature type reference). All entries are lowercase for case-insensitive
 * lookup — the API emits lowercase type ids like "goblin", "wizard".
 */

const CREATURE_SUBTYPES_RAW = [
    'Advisor', 'Aetherborn', 'Alien', 'Ally', 'Angel', 'Antelope', 'Ape', 'Archer', 'Archon', 'Armadillo',
    'Army', 'Artificer', 'Assassin', 'Assembly-Worker', 'Astartes', 'Atog', 'Aurochs', 'Avatar', 'Azra',
    'Badger', 'Balloon', 'Barbarian', 'Bard', 'Basilisk', 'Bat', 'Bear', 'Beast', 'Beaver', 'Beeble',
    'Beholder', 'Berserker', 'Bird', 'Blinkmoth', 'Boar', 'Bringer', 'Brushwagg', 'Camarid', 'Camel',
    'Capybara', 'Caribou', 'Carrier', 'Cat', 'Centaur', 'Child', 'Chimera', 'Citizen', 'Cleric', 'Clown',
    'Cockatrice', 'Construct', 'Coward', 'Coyote', 'Crab', 'Crocodile', "C'tan", 'Custodes', 'Cyberman',
    'Cyclops', 'Dalek', 'Dauthi', 'Demigod', 'Demon', 'Deserter', 'Detective', 'Devil', 'Dinosaur', 'Djinn',
    'Doctor', 'Dog', 'Dragon', 'Drake', 'Dreadnought', 'Drix', 'Drone', 'Druid', 'Dryad', 'Dwarf',
    'Efreet', 'Egg', 'Elder', 'Eldrazi', 'Elemental', 'Elephant', 'Elf', 'Elk', 'Employee', 'Eye',
    'Faerie', 'Ferret', 'Fish', 'Flagbearer', 'Fox', 'Fractal', 'Frog', 'Fungus',
    'Gamer', 'Gargoyle', 'Germ', 'Giant', 'Gith', 'Glimmer', 'Gnoll', 'Gnome', 'Goat', 'Goblin', 'God',
    'Golem', 'Gorgon', 'Graveborn', 'Gremlin', 'Griffin', 'Guest',
    'Hag', 'Halfling', 'Hamster', 'Harpy', 'Hedgehog', 'Hellion', 'Hero', 'Hippo', 'Hippogriff', 'Homarid',
    'Homunculus', 'Horror', 'Horse', 'Human', 'Hydra', 'Hyena',
    'Illusion', 'Imp', 'Incarnation', 'Inkling', 'Inquisitor', 'Insect',
    'Jackal', 'Jellyfish', 'Juggernaut',
    'Kavu', 'Kirin', 'Kithkin', 'Knight', 'Kobold', 'Kor', 'Kraken',
    'Lamia', 'Lammasu', 'Leech', 'Leviathan', 'Lhurgoyf', 'Licid', 'Lizard', 'Llama', 'Lobster',
    'Manticore', 'Masticore', 'Mercenary', 'Merfolk', 'Metathran', 'Minion', 'Minotaur', 'Mite', 'Mole',
    'Monger', 'Mongoose', 'Monk', 'Monkey', 'Moogle', 'Moonfolk', 'Mount', 'Mouse', 'Mutant', 'Myr',
    'Mystic',
    'Nautilus', 'Necron', 'Nephilim', 'Nightmare', 'Nightstalker', 'Ninja', 'Noble', 'Noggle', 'Nomad',
    'Nymph',
    'Octopus', 'Ogre', 'Ooze', 'Orb', 'Orc', 'Orgg', 'Otter', 'Ouphe', 'Ox', 'Oyster',
    'Pangolin', 'Peasant', 'Pegasus', 'Pentavite', 'Performer', 'Pest', 'Phelddagrif', 'Phoenix',
    'Phyrexian', 'Pilot', 'Pincher', 'Pirate', 'Plant', 'Porcupine', 'Possom', 'Praetor', 'Primarch',
    'Prism', 'Processor',
    'Qu',
    'Rabbit', 'Raccoon', 'Ranger', 'Rat', 'Rebel', 'Reflection', 'Rhino', 'Rigger', 'Robot', 'Rogue',
    'Sable', 'Salamander', 'Samurai', 'Sand', 'Saproling', 'Satyr', 'Scarecrow', 'Scientist', 'Scion',
    'Scorpion', 'Scout', 'Sculpture', 'Serf', 'Serpent', 'Servo', 'Shade', 'Shaman', 'Shapeshifter',
    'Shark', 'Sheep', 'Siren', 'Skeleton', 'Skunk', 'Slith', 'Sliver', 'Sloth', 'Slug', 'Snail', 'Snake',
    'Soldier', 'Soltari', 'Spawn', 'Specter', 'Spellshaper', 'Sphinx', 'Spider', 'Spike', 'Spirit',
    'Splinter', 'Sponge', 'Squid', 'Squirrel', 'Starfish', 'Surrakar', 'Survivor', 'Symbiote', 'Synth',
    'Tentacle', 'Tetravite', 'Thalakos', 'Thopter', 'Thrull', 'Tiefling', 'Time Lord', 'Toy', 'Treefolk',
    'Trilobite', 'Triskelavite', 'Troll', 'Turtle', 'Tyranid',
    'Unicorn',
    'Vampire', 'Varmint', 'Vedalken', 'Villain', 'Volver',
    'Wall', 'Walrus', 'Warlock', 'Warrior', 'Weasel', 'Weird', 'Werewolf', 'Whale', 'Wizard', 'Wolf',
    'Wolverine', 'Wombat', 'Worm', 'Wraith', 'Wurm',
    'Yeti',
    'Zombie', 'Zubera',
];

/**
 * Extra non-subtype tokens the backend archetype pipeline may legitimately
 * surface as "kindreds" outside of the subtype list. `legendary` is a
 * supertype but appears in the spec example because the commander itself
 * is legendary. `changeling` is an ability but is sometimes counted as a
 * virtual tribal signal.
 */
const EXTRA_ALLOWED = ['legendary', 'changeling'];

export const VALID_KINDRED_TYPES = new Set([
    ...CREATURE_SUBTYPES_RAW.map((t) => t.toLowerCase()),
    ...EXTRA_ALLOWED,
]);

export const isValidKindredType = (type) => {
    if (!type || typeof type !== 'string') return false;
    return VALID_KINDRED_TYPES.has(type.toLowerCase());
};
